import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CheckCircle2, Camera, Upload, AlertTriangle, Car, Navigation } from 'lucide-react';
import { PURPOSE_OPTIONS, STATUS_COLORS, getDays, calcTotalExpense } from './constants';

export const PlanDetailDialog = ({
  plan, onClose, onCheckin, onCheckout, onComplete, onBillUpload, onStartCamera, billInputRef,
}) => {
  if (!plan) return null;

  const p = plan;
  const purpose = PURPOSE_OPTIONS.find(o => o.value === p.purpose)?.label || p.purpose;
  const days = getDays(p.start_date, p.end_date);
  const totalExp = calcTotalExpense(p.daily_expenses);
  const allCheckedOut = (p.locations || []).every(l => l.actual_checkout);

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="plan-detail-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg font-bold">{p.visit_type === 'client_visit' ? 'Client Visit' : 'Business Location Visit'}</DialogTitle>
            <Badge className={`${STATUS_COLORS[p.status]}`}>{p.status.replace('_', ' ')}</Badge>
          </div>
          <p className="text-sm text-slate-500">{purpose} | {p.start_date} to {p.end_date} ({days} days)</p>
          {(p.mode_of_travel || p.total_distance_km > 0) && (
            <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
              {p.mode_of_travel && (
                <span className="flex items-center gap-1">
                  <Car className="w-4 h-4" /> {p.mode_of_travel}
                </span>
              )}
              {p.total_distance_km > 0 && (
                <span className="flex items-center gap-1">
                  <Navigation className="w-4 h-4" /> {p.total_distance_km} km
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          {p.start_selfie && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-xs font-bold text-emerald-800 mb-2">Start Selfie</p>
              <img src={decodeURIComponent(p.start_selfie)} alt="Start selfie" className="w-24 h-24 object-cover rounded-lg" />
              <p className="text-xs text-emerald-600 mt-1">GPS: {p.start_lat?.toFixed(4)}, {p.start_lng?.toFixed(4)}</p>
            </div>
          )}

          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">Locations ({(p.locations || []).length})</h3>
            {(p.locations || []).map((loc, i) => {
              const hasDeviation = (p.deviations || []).some(d => d.location_index === i);
              return (
                <div key={i} className={`p-3 border rounded-lg mb-2 ${hasDeviation ? 'border-amber-300 bg-amber-50' : 'border-border/40 bg-white'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{loc.place_name || `Location ${i + 1}`}</p>
                      <p className="text-xs text-slate-400">Planned: {loc.planned_checkin} - {loc.planned_checkout} ({loc.planned_duration_mins} mins)</p>
                      {loc.actual_checkin && <p className="text-xs text-emerald-600 mt-0.5">Checked in: {new Date(loc.actual_checkin).toLocaleTimeString()}</p>}
                      {loc.checkin_validation && !loc.checkin_validation.is_valid && (
                        <p className="text-xs text-amber-600">⚠️ Check-in was {loc.checkin_validation.distance_meters}m away (max: {loc.checkin_validation.tolerance_meters}m)</p>
                      )}
                      {loc.actual_checkout && <p className="text-xs text-blue-600">Checked out: {new Date(loc.actual_checkout).toLocaleTimeString()} | Actual: {loc.actual_duration_mins} mins</p>}
                      {loc.checkout_validation && !loc.checkout_validation.is_valid && (
                        <p className="text-xs text-amber-600">⚠️ Check-out was {loc.checkout_validation.distance_meters}m away (max: {loc.checkout_validation.tolerance_meters}m)</p>
                      )}
                      {hasDeviation && <p className="text-xs text-amber-700 font-semibold mt-0.5"><AlertTriangle className="w-3 h-3 inline mr-1" />Duration deviation detected</p>}
                    </div>
                    {p.status === 'in_progress' && (
                      <div className="flex gap-1">
                        {!loc.actual_checkin && <Button size="sm" onClick={() => onCheckin(p.id, i)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">Check In</Button>}
                        {loc.actual_checkin && !loc.actual_checkout && <Button size="sm" onClick={() => onCheckout(p.id, i)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7">Check Out</Button>}
                        {loc.actual_checkout && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">Expenses</h3>
            <div className="border border-border/40 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 border-b"><th className="p-2 text-left">Day</th><th className="p-2">Breakfast</th><th className="p-2">Lunch</th><th className="p-2">Dinner</th><th className="p-2">Night Stay</th></tr></thead>
                <tbody>{(p.daily_expenses || []).map((e, i) => (
                  <tr key={i} className="border-b border-slate-50"><td className="p-2 font-semibold">Day {e.day}</td><td className="p-2 text-center">{e.breakfast}</td><td className="p-2 text-center">{e.lunch}</td><td className="p-2 text-center">{e.dinner}</td><td className="p-2 text-center">{e.night_stay >= 0 ? e.night_stay : '-'}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <p className="text-right text-sm font-bold text-slate-900 mt-2">Total: Rs.{totalExp.toLocaleString()}</p>
          </div>

          {(p.expense_bills || []).length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2">Uploaded Bills ({p.expense_bills.length})</h3>
              <div className="flex flex-wrap gap-2">{p.expense_bills.map((b, i) => (
                <img key={i} src={decodeURIComponent(b)} alt={`Bill ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-border/40" />
              ))}</div>
            </div>
          )}

          {p.deviations?.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="text-sm font-bold text-amber-800 mb-1"><AlertTriangle className="w-4 h-4 inline mr-1" />Deviations</h3>
              {p.deviations.map((d, i) => <p key={i} className="text-xs text-amber-700">Location {d.location_index + 1}: Planned {d.planned_mins} mins, Actual {d.actual_mins} mins</p>)}
            </div>
          )}

          {p.notes && <div><h3 className="text-sm font-bold text-slate-700 mb-1">Notes</h3><p className="text-sm text-slate-600">{p.notes}</p></div>}
        </div>

        <DialogFooter>
          {p.status === 'approved' && (
            <Button data-testid="start-plan-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2" onClick={onStartCamera}>
              <Camera className="w-4 h-4" /> Start Plan (Take Selfie + GPS)
            </Button>
          )}
          {p.status === 'in_progress' && allCheckedOut && (
            <Button data-testid="complete-plan-btn" className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-2" onClick={() => onComplete(p.id)}>
              <CheckCircle2 className="w-4 h-4" /> Complete Plan
            </Button>
          )}
          {p.status === 'completed' && (
            <div className="w-full">
              <input ref={billInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onBillUpload(p.id, e)} />
              <Button data-testid="upload-bills-btn" variant="outline" className="w-full gap-2" onClick={() => billInputRef.current?.click()}>
                <Upload className="w-4 h-4" /> Upload Expense Bills
              </Button>
              {(p.expense_bills || []).length > 0 && <p className="text-xs text-slate-400 text-center mt-2">{p.expense_bills.length} bill(s) uploaded</p>}
            </div>
          )}
          {p.status === 'pending_approval' && (
            <p className="text-xs text-amber-600 text-center w-full">Waiting for admin approval...</p>
          )}
          {p.status === 'rejected' && (
            <p className="text-xs text-red-500 text-center w-full">This plan was rejected. {p.approval_remarks && `Remarks: ${p.approval_remarks}`}</p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
